import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

import { Store } from "../../../types/Store";
import NotificationsFragment from "./components/notification/NotificationFragment";

import StoreUser from "../../../types/StoreUser";
import fetchAllNotifications from "../../../database/notifications/fetchAllNotifications";
import EmailAutomationPanel from "./components/email/EmailAutomationPanel";
import { OutreachDashboard } from "../outreach";
import AppsBoard from "../outreach/components/AppsBoard";
import UniboxPanel from "./UniboxPanel";
import { getUniboxThreads } from "../../../database/community/unibox";
import { isBoardOperator } from "../../../types/Board";

import { DesignSystemSurface, PageHeader } from "../../../design-system";
import {
  IconCompass,
  IconBell,
  IconUsers,
  IconKanban,
} from "../../../design-system/icons/icons";

const Page = styled.div`
  min-height: calc(100vh - 104px);
  padding: var(--space-6) var(--space-6) var(--space-16);
`;

const PageInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const TabBar = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 4px;
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-6);
`;

const TabBadge = styled.span`
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-pill);
  background: var(--color-accent);
  color: #fff;
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
`;

const TabButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);

  &:hover {
    color: var(--color-text);
  }

  ${(p) =>
    p.$active &&
    css`
      background: var(--color-surface);
      color: var(--color-text);
      font-weight: var(--font-weight-semibold);
      box-shadow: var(--shadow-xs);
    `}
`;

type Props = {
  store: Store;
  isMobile: boolean;
  storeUser: StoreUser;
};

// Messaging brings all outbound communication under one roof: cold outreach to
// prospects (the Outreach tab, Tribed-global) and newsletters + pushes to
// members (the Newsletter & Push tab, per-community).
const Messaging = (props: Props) => {
  const { isMobile, storeUser } = props;
  // Land on Unibox (id 2) — the highest-priority surface.
  const [tab, setTab] = useState(2);
  const [uniboxUnread, setUniboxUnread] = useState(0);

  // Unread signal for the Unibox tab, visible without opening it.
  useEffect(() => {
    const communityId = storeUser?.communityId;
    if (!communityId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await getUniboxThreads({ communityId, unreadOnly: true, limit: 100 });
        if (!cancelled) setUniboxUnread(res.unreadTotal || 0);
      } catch {
        // Badge is best-effort; the panel surfaces real errors.
      }
    };
    poll();
    const id = setInterval(poll, 60000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [storeUser?.communityId]);

  const notificationsData = fetchAllNotifications({
    communityId: storeUser?.communityId,
    isRead: true,
  });

  useEffect(() => {
    if (!("Notification" in window)) {
      console.log("This browser does not support desktop notification");
    } else {
      Notification.requestPermission();
    }
  }, []);

  // The Apps portfolio board is a Tribed-operator surface, gated the same way as
  // the issue board (home community is digital_university or tribed).
  const showApps = isBoardOperator({ communityId: storeUser?.communityId });

  // Order = priority. Unibox (live replies from prospects and members) is the
  // most time-sensitive and leads; Newsletter & Push (scheduled broadcast) is
  // the least urgent and sits last. Ids are stable content routes — only the
  // array order changes what shows first.
  const TABS = [
    { id: 2, label: "Unibox", icon: <IconUsers size={14} /> },
    { id: 1, label: "Outreach", icon: <IconCompass size={14} /> },
    ...(showApps
      ? [{ id: 3, label: "Apps", icon: <IconKanban size={14} /> }]
      : []),
    { id: 0, label: "Newsletter & Push", icon: <IconBell size={14} /> },
  ];

  return (
    <DesignSystemSurface>
      <Page>
        <PageInner>
          <PageHeader
            title="Messaging"
            subtitle="Outbound communication: cold outreach to prospects, plus newsletters and pushes to your members."
          />

          <TabBar role="tablist">
            {TABS.map((t) => (
              <TabButton
                key={t.id}
                type="button"
                role="tab"
                $active={tab === t.id}
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
              >
                {t.icon}
                {t.label}
                {t.id === 2 && uniboxUnread > 0 ? (
                  <TabBadge>{uniboxUnread > 99 ? "99+" : uniboxUnread}</TabBadge>
                ) : null}
              </TabButton>
            ))}
          </TabBar>

          {tab === 0 && (
            <>
              <EmailAutomationPanel communityId={storeUser?.communityId} />
              <NotificationsFragment
                notifications={notificationsData?.notifications || []}
                isMobile={isMobile}
                communityId={storeUser?.communityId || ""}
              />
            </>
          )}

          {tab === 1 && (
            <OutreachDashboard communityId={storeUser?.communityId || ""} />
          )}

          {tab === 3 && showApps && <AppsBoard />}

          {tab === 2 && <UniboxPanel communityId={storeUser?.communityId || ""} />}
        </PageInner>
      </Page>
    </DesignSystemSurface>
  );
};

const mapStateToProps = (state) => ({
  store: state.store,
  isMobile: state.isMobile,
  storeUser: state.storeUser,
  adminConfig: state.adminConfig,
});

const mapDispatchToProps = (_dispatch) => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(Messaging);
